import { useMemo } from 'react'

import type { AppointmentItem } from '../../appointments/appointments-model'
import {
  appointmentLaneKey,
  appointmentMinute,
  calendarDayNumber,
  calendarWeekday,
  compareLanes,
  countLabel,
  formatCount,
  laneLabel,
  shortCalendarDate,
  type CalendarGrouping,
} from '../calendar-model'
import {
  clockLabel,
  displayRange,
  hoursLabel,
  UNKNOWN_DURATION_MINUTES,
  type TimedEntry,
} from '../calendar-layout'
import { LaneAvatar } from './LaneAvatar'
import { StatusMeter, statusCounts } from './StatusMeter'
import styles from './CalendarWeekView.module.css'

function entryFor(item: AppointmentItem): TimedEntry<AppointmentItem> | null {
  const start = appointmentMinute(item)
  if (start === null) return null
  const duration =
    item.scheduledAtMs !== null &&
    item.scheduledEndMs !== null &&
    item.scheduledEndMs > item.scheduledAtMs
      ? (item.scheduledEndMs - item.scheduledAtMs) / 60_000
      : null
  return {
    item,
    startMinute: start,
    endMinute: duration === null ? null : start + duration,
  }
}

function entryEnd(entry: TimedEntry<unknown>) {
  return entry.endMinute ?? entry.startMinute + UNKNOWN_DURATION_MINUTES
}

/**
 * The week as providers (or rooms) by day: how many visits each has, when
 * their day starts and ends, and where it is booked. Opening a cell shows
 * that day with the lane in view.
 */
export function CalendarWeekView({
  days,
  appointmentsByDate,
  grouping,
  today,
  onOpenDay,
}: {
  days: string[]
  appointmentsByDate: Map<string, AppointmentItem[]>
  grouping: CalendarGrouping
  today: string
  onOpenDay: (date: string, lane?: string) => void
}) {
  const model = useMemo(() => {
    const byDay = new Map<string, Array<TimedEntry<AppointmentItem>>>()
    for (const date of days) {
      byDay.set(
        date,
        (appointmentsByDate.get(date) ?? []).flatMap((item) => {
          const entry = entryFor(item)
          return entry ? [entry] : []
        })
      )
    }
    const all = [...byDay.values()].flat()
    const lanes = Array.from(
      new Set(all.map((entry) => appointmentLaneKey(entry.item, grouping)))
    ).sort(compareLanes)
    return { byDay, lanes, range: displayRange(all) }
  }, [appointmentsByDate, days, grouping])
  const span = model.range.endMinute - model.range.startMinute
  const percent = (minute: number) =>
    `${((minute - model.range.startMinute) / span) * 100}%`

  return (
    <section className={styles.week} aria-label="Week calendar">
      {model.lanes.length === 0 ? (
        <p className={styles.empty}>No appointments this week.</p>
      ) : null}
      <div className={styles.scroller}>
        <table className={styles.table}>
          <caption className={styles.srOnly}>
            {grouping === 'room' ? 'Rooms' : 'Providers'} by day
          </caption>
          <thead>
            <tr>
              <th scope="col" className={styles.corner}>
                {grouping === 'room' ? 'Room' : 'Provider'}
              </th>
              {days.map((date) => {
                const items = appointmentsByDate.get(date) ?? []
                const counts = statusCounts(items)
                return (
                  <th
                    scope="col"
                    key={date}
                    className={styles.dayHeader}
                    data-today={date === today ? 'true' : undefined}
                    aria-current={date === today ? 'date' : undefined}
                  >
                    <button
                      type="button"
                      aria-label={`${shortCalendarDate(date)}: ${countLabel(items.length, 'appointment')}, ${counts.confirmed} confirmed. Open day`}
                      onClick={() => onOpenDay(date)}
                    >
                      <span className={styles.weekday}>
                        {calendarWeekday(date)}
                      </span>
                      <span className={styles.dayNumber}>
                        {calendarDayNumber(date)}
                      </span>
                      <span className={styles.dayTotal}>
                        {items.length
                          ? countLabel(items.length, 'appointment')
                          : 'No appointments'}
                      </span>
                      {items.length ? (
                        <>
                          <StatusMeter counts={counts} />
                          <span className={styles.dayConfirmed}>
                            {formatCount(counts.confirmed)} confirmed
                          </span>
                        </>
                      ) : null}
                    </button>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {model.lanes.map((lane) => {
              const laneTotal = days.reduce(
                (total, date) =>
                  total +
                  (model.byDay.get(date) ?? []).filter(
                    (entry) => appointmentLaneKey(entry.item, grouping) === lane
                  ).length,
                0
              )
              return (
                <tr key={lane}>
                  <th scope="row" className={styles.laneHeader}>
                    <span className={styles.laneName}>
                      <LaneAvatar label={laneLabel(lane)} grouping={grouping} />
                      <span>
                        <strong title={laneLabel(lane)}>
                          {laneLabel(lane)}
                        </strong>
                        <small>{countLabel(laneTotal, 'appointment')}</small>
                      </span>
                    </span>
                  </th>
                  {days.map((date) => {
                    const entries = (model.byDay.get(date) ?? []).filter(
                      (entry) =>
                        appointmentLaneKey(entry.item, grouping) === lane
                    )
                    const today_ = date === today ? 'true' : undefined
                    if (!entries.length) {
                      return (
                        <td
                          className={styles.emptyCell}
                          data-today={today_}
                          key={date}
                        >
                          <span className={styles.srOnly}>No appointments</span>
                        </td>
                      )
                    }
                    const first = Math.min(
                      ...entries.map((entry) => entry.startMinute)
                    )
                    const last = Math.max(...entries.map(entryEnd))
                    return (
                      <td
                        className={styles.cellWrap}
                        data-today={today_}
                        key={date}
                      >
                        <button
                          type="button"
                          className={styles.cell}
                          aria-label={`${shortCalendarDate(date)}, ${laneLabel(lane)}: ${countLabel(entries.length, 'appointment')}, ${clockLabel(first)} to ${clockLabel(last)}. Open day`}
                          onClick={() => onOpenDay(date, lane)}
                        >
                          <span className={styles.cellCount}>
                            {formatCount(entries.length)}
                          </span>
                          <span className={styles.cellHours}>
                            {hoursLabel([
                              { startMinute: first, endMinute: last },
                            ])}
                          </span>
                          <span className={styles.track} aria-hidden="true">
                            {entries.map((entry) => (
                              <i
                                key={entry.item.appointmentId}
                                style={{
                                  left: percent(entry.startMinute),
                                  width: `calc(${percent(entryEnd(entry))} - ${percent(entry.startMinute)})`,
                                }}
                              />
                            ))}
                          </span>
                        </button>
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}
